import React, { useCallback, useEffect, useState } from "react";
import { cn } from "../lib/utils";
import { usePrefersReducedMotion } from "../hooks/usePrefersReducedMotion";

const STORAGE_KEY = "instant3d:viewer-hint-dismissed";
const AUTO_DISMISS_MS = 8000;

const wasDismissed = (): boolean => {
  try {
    return localStorage.getItem(STORAGE_KEY) === "true";
  } catch {
    return false;
  }
};

/**
 * One-time hint teaching the standard viewer controls the first time a model
 * loads. Auto-dismisses after a few seconds; never shown again once dismissed.
 * Positioned above the wireframe FloatingToolbar (fixed bottom-8).
 */
function ViewerControlsHint() {
  const [visible, setVisible] = useState(() => !wasDismissed());
  const prefersReducedMotion = usePrefersReducedMotion();

  const dismiss = useCallback(() => {
    setVisible(false);
    try {
      localStorage.setItem(STORAGE_KEY, "true");
    } catch {
      // Best-effort persistence; worst case the hint shows again next session.
    }
  }, []);

  useEffect(() => {
    if (!visible) return;
    const timeout = setTimeout(dismiss, AUTO_DISMISS_MS);
    return () => clearTimeout(timeout);
  }, [visible, dismiss]);

  if (!visible) return null;

  return (
    <div
      role="note"
      className={cn(
        "fixed bottom-24 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3",
        "bg-secondary-background border-2 border-border rounded-lg neo-shadow px-4 py-2",
        !prefersReducedMotion && "animate-fadeIn",
      )}
    >
      <span className="text-sm font-medium text-foreground whitespace-nowrap">
        Drag to orbit · Scroll to zoom · Right-click drag to pan
      </span>
      <button
        type="button"
        aria-label="Dismiss viewer hint"
        onClick={dismiss}
        className="text-foreground/60 hover:text-foreground rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)]"
      >
        ✕
      </button>
    </div>
  );
}

export default ViewerControlsHint;
